<template>
  <div class="data-config page-shell">
    <div class="toolbar">
      <span class="hint">
        从 Top500 池中选择股票，配置为「重点关注」，供「个股趋势」查询使用。
        鼠标悬停可查看 2026Q2 持有家数与持股市值。
      </span>
      <el-button type="primary" size="small" :loading="saving" @click="onSave">
        保存配置
      </el-button>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="error-alert"
    />

    <div v-loading="loading" element-loading-text="加载中...">
      <el-transfer
        v-model="selectedCodes"
        filterable
        :titles="['Top500 池', '重点关注']"
        :button-texts="['移除', '添加']"
        :data="transferData"
        :props="{ key: 'key', label: 'label' }"
        :filter-method="filterMethod"
        filter-placeholder="代码 / 名称 / 行业"
        class="focus-transfer"
      >
        <span slot-scope="{ option }" class="transfer-item">
          <el-tooltip
            placement="right"
            effect="dark"
            :open-delay="200"
            popper-class="focus-stock-tooltip"
          >
            <div slot="content" class="tooltip-content">
              <div class="tip-title">{{ option.name }}（{{ option.code }}）</div>
              <div class="tip-row">季度：{{ option.quarter || '2026Q2' }}</div>
              <div class="tip-row">市值排名：#{{ option.rank }}</div>
              <div class="tip-row">所属行业：{{ option.industry || '未分类' }}</div>
              <div class="tip-row">持有家数：{{ formatShares(option.shares) }} 家</div>
              <div class="tip-row">持股市值：{{ formatMv(option.marketValue) }} 亿元</div>
            </div>
            <span class="item-text">{{ option.label }}</span>
          </el-tooltip>
        </span>
      </el-transfer>

      <div class="summary">
        已选 {{ selectedCodes.length }} / {{ pool.length }}
        <span v-if="selectedCodes.length" class="selected-tags">
          <el-tag
            v-for="code in selectedCodes.slice(0, 12)"
            :key="code"
            size="mini"
            type="info"
            class="tag"
          >
            {{ labelOf(code) }}
          </el-tag>
          <span v-if="selectedCodes.length > 12">…</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchTop500Pool, fetchFocusStocks, saveFocusStocks } from '../utils/focusApi.js'

export default {
  name: 'DataConfig',
  data() {
    return {
      pool: [],
      selectedCodes: [],
      loading: false,
      saving: false,
      error: '',
    }
  },
  computed: {
    poolMap() {
      return new Map(this.pool.map((item) => [item.code, item]))
    },
    transferData() {
      return this.pool.map((item) => ({
        key: item.code,
        label: `${item.code} ${item.name} · ${item.industry || '未分类'} · #${item.rank}`,
        code: item.code,
        name: item.name,
        industry: item.industry || '',
        rank: item.rank,
        shares: item.shares,
        marketValue: item.marketValue,
        quarter: item.quarter || '2026Q2',
      }))
    },
  },
  async created() {
    await this.loadAll()
  },
  methods: {
    labelOf(code) {
      const item = this.poolMap.get(code)
      return item ? `${item.name}(${code})` : code
    },
    formatShares(v) {
      if (v == null || v === '') return '--'
      const n = Number(v)
      return Number.isFinite(n) ? String(n) : String(v)
    },
    formatMv(v) {
      if (v == null || v === '') return '--'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : String(v)
    },
    filterMethod(query, item) {
      const q = String(query || '').trim().toLowerCase()
      if (!q) return true
      return String(item.label || '').toLowerCase().includes(q)
    },
    async loadAll() {
      this.loading = true
      this.error = ''
      try {
        const [pool, focus] = await Promise.all([
          fetchTop500Pool(),
          fetchFocusStocks(),
        ])
        this.pool = pool
        this.selectedCodes = focus.map((item) => item.code)
      } catch (err) {
        this.error = err.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async onSave() {
      this.saving = true
      this.error = ''
      try {
        const list = await saveFocusStocks(this.selectedCodes)
        this.selectedCodes = list.map((item) => item.code)
        this.$message.success(`已保存重点关注 ${list.length} 只股票`)
      } catch (err) {
        this.error = err.message || '保存失败'
        this.$message.error(this.error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.data-config {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.hint {
  font-size: 13px;
  color: #606266;
}

.error-alert {
  margin-bottom: 12px;
}

.focus-transfer {
  display: flex;
  justify-content: flex-start;
}

.focus-transfer >>> .el-transfer-panel {
  width: 360px;
}

.focus-transfer >>> .el-transfer-panel__body {
  height: 480px;
}

.focus-transfer >>> .el-transfer-panel__list.is-filterable {
  height: 420px;
}

.focus-transfer >>> .el-transfer-panel__item {
  overflow: visible;
}

.transfer-item {
  display: inline-block;
  max-width: 280px;
  vertical-align: middle;
}

.item-text {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.summary {
  margin-top: 12px;
  font-size: 13px;
  color: #606266;
}

.selected-tags {
  margin-left: 8px;
}

.tag {
  margin: 0 4px 4px 0;
}
</style>

<style>
.focus-stock-tooltip {
  max-width: 280px;
  line-height: 1.6;
}

.focus-stock-tooltip .tip-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.focus-stock-tooltip .tip-row {
  font-size: 12px;
}
</style>
